export const SAVE_CARGA_MODOS = ["exemplo", "estudo"] as const;

export type SaveCargaModo = (typeof SAVE_CARGA_MODOS)[number];

export type SaveCargaEntrada = {
  demandaMaximaKw: number;
  demandaMinimaKw: number;
  demandaMediaKw: number;
  demandaRamalKw: number;
  qtdPontos: number;
  potenciaPontoKw: number;
  demandaVeraoKw: number;
};

export type SaveCargaCalculo = SaveCargaEntrada & {
  cargaTotalSemControleKw: number;
  somaSemControleKw: number;
  margemKw: number;
  margemDisponivelMaxKw: number;
  recargasSimultaneas: number;
  recargasSimultaneasMax: number;
  temFaixaDisponivel: boolean;
  estouraRamalSemControle: boolean;
  margemVeraoKw: number;
  recargasSimultaneasVerao: number;
  temCenarioVerao: boolean;
};

/** Exemplo ilustrativo aprovado para propostas sem estudo de carga. */
export const SAVE_CARGA_EXEMPLO: SaveCargaEntrada = {
  demandaMaximaKw: 80,
  demandaMinimaKw: 10,
  demandaMediaKw: 0,
  demandaRamalKw: 150,
  qtdPontos: 50,
  potenciaPontoKw: 7.4,
  demandaVeraoKw: 0,
};

export const SAVE_CARGA_POTENCIA_PADRAO_KW = 7.4;

export function isSaveCargaModo(value: unknown): value is SaveCargaModo {
  return value === "exemplo" || value === "estudo";
}

export function parseSaveCargaModo(value: unknown): SaveCargaModo {
  return value === "estudo" ? "estudo" : "exemplo";
}

function numeroNaoNegativo(value: unknown): number {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

function inteiroMinimo(value: unknown, minimo: number): number {
  const n = Math.floor(Number(value) || 0);
  return Number.isFinite(n) && n >= minimo ? n : minimo;
}

export function calcularControleDemanda(entrada: SaveCargaEntrada): SaveCargaCalculo {
  const demandaMaximaKw = numeroNaoNegativo(entrada.demandaMaximaKw);
  const demandaMinimaKw = numeroNaoNegativo(entrada.demandaMinimaKw);
  const demandaMediaKw = numeroNaoNegativo(entrada.demandaMediaKw);
  const demandaRamalKw = numeroNaoNegativo(entrada.demandaRamalKw);
  const qtdPontos = inteiroMinimo(entrada.qtdPontos, 1);
  const potenciaPontoKw = numeroNaoNegativo(entrada.potenciaPontoKw) || SAVE_CARGA_POTENCIA_PADRAO_KW;
  const demandaVeraoKw = numeroNaoNegativo(entrada.demandaVeraoKw);

  const cargaTotalSemControleKw = qtdPontos * potenciaPontoKw;
  const somaSemControleKw = demandaMaximaKw + cargaTotalSemControleKw;
  const margemKw = demandaRamalKw - demandaMaximaKw;
  const temFaixaDisponivel = demandaMinimaKw > 0;
  const margemDisponivelMaxKw = temFaixaDisponivel
    ? demandaRamalKw - demandaMinimaKw
    : margemKw;
  const recargasSimultaneas =
    potenciaPontoKw > 0 ? Math.max(0, Math.floor(margemKw / potenciaPontoKw)) : 0;
  const recargasSimultaneasMax =
    potenciaPontoKw > 0
      ? Math.max(0, Math.floor(margemDisponivelMaxKw / potenciaPontoKw))
      : 0;
  const margemVeraoKw = demandaRamalKw - demandaVeraoKw;
  const recargasSimultaneasVerao =
    potenciaPontoKw > 0 ? Math.max(0, Math.floor(margemVeraoKw / potenciaPontoKw)) : 0;

  return {
    demandaMaximaKw,
    demandaMinimaKw,
    demandaMediaKw,
    demandaRamalKw,
    qtdPontos,
    potenciaPontoKw,
    demandaVeraoKw,
    cargaTotalSemControleKw,
    somaSemControleKw,
    margemKw,
    margemDisponivelMaxKw,
    recargasSimultaneas,
    recargasSimultaneasMax,
    temFaixaDisponivel,
    estouraRamalSemControle: somaSemControleKw > demandaRamalKw,
    margemVeraoKw,
    recargasSimultaneasVerao,
    temCenarioVerao: demandaVeraoKw > 0,
  };
}

export function calcularExemploControleDemanda(): SaveCargaCalculo {
  return calcularControleDemanda(SAVE_CARGA_EXEMPLO);
}

export function validarSaveCargaEstudo(entrada: SaveCargaEntrada): string | null {
  if (numeroNaoNegativo(entrada.demandaMaximaKw) <= 0) {
    return "Informe a demanda máxima medida (kW).";
  }
  if (numeroNaoNegativo(entrada.demandaRamalKw) <= 0) {
    return "Informe a demanda do ramal / capacidade instalada (kW).";
  }
  if (inteiroMinimo(entrada.qtdPontos, 0) < 1) {
    return "Informe a quantidade de pontos previstos (mínimo 1).";
  }
  if (numeroNaoNegativo(entrada.potenciaPontoKw) <= 0) {
    return "Informe a potência do ponto de recarga (kW).";
  }
  return null;
}

export function formatarKw(value: number, digits = 1): string {
  return `${(Number(value) || 0).toLocaleString("pt-BR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: digits,
  })} kW`;
}

export function rotuloSaveCargaModo(modo: SaveCargaModo): string {
  switch (modo) {
    case "exemplo":
      return "Exemplo ilustrativo (sem estudo de carga)";
    case "estudo":
      return "Dados do estudo de viabilidade de carga";
    default: {
      const _never: never = modo;
      return _never;
    }
  }
}
